import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { ConfigProvider, Upload } from 'antd';
import type { RcFile, UploadProps } from 'antd/es/upload/interface';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import type { MultilanguageErrorState, MultilanguageState } from '../../hooks/useMultilanguageState';
import commonStyles from '../../style/components/common.module.scss';
import { createPrimaryTheme, createSecondaryTheme } from '../../theme/antd-theme';
import useNotification from '../Notification';
import type { ContentImage } from '../../schemas/auth';

export type ImageUploadTheme = 'primary' | 'secondary';

interface ImageUploadProps {
  label?: string;
  theme?: ImageUploadTheme;
  error?: boolean | MultilanguageErrorState;
  errorMessage?: string;
  onChange: (file?: ContentImage | MultilanguageState<ContentImage | undefined>) => void;
  value?: ContentImage | MultilanguageState<ContentImage>;
  initialImageUrl?: string;
  maxSize?: number;
  acceptedFormats?: string[];
  maxWidth?: number;
  maxHeight?: number;
  aspectRatio?: number;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  currentLanguage?: string;
  attempted?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  theme = 'primary',
  error = false,
  errorMessage,
  onChange,
  currentLanguage,
  value,
  maxSize = 5,
  acceptedFormats = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/gif'],
  maxWidth,
  maxHeight,
  aspectRatio,
  placeholder = 'Click to upload or drag and drop',
  className = '',
  disabled = false,
  required = false,
}) => {
  const { showNotification } = useNotification();
  const [imagePreview, setImagePreview] = useState<string | MultilanguageState<string>>();

  useEffect(() => {
    if (imagePreview) return;

    if (typeof value === 'object' && currentLanguage) {
      const val = value as MultilanguageState<ContentImage>;
      if (val[currentLanguage] && typeof val[currentLanguage]?.document === 'string') {
        const previews = {} as MultilanguageState<string>;
        for (const key in val) {
          previews[key] = val[key]?.document || '';
        }
        setImagePreview(previews);
      }
    } else if (typeof value?.document === 'string') {
      setImagePreview(value.document);
    }
  }, [value]);

  const getThemeConfig = () => {
    switch (theme) {
      case 'secondary':
        return createSecondaryTheme();
      default:
        return createPrimaryTheme();
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isValidFormat = acceptedFormats.includes(file.type);
    if (!isValidFormat) {
      const formatNames = acceptedFormats.map((format) => format.replace('image/', '').toUpperCase()).join(', ');
      showNotification({
        type: 'error',
        message: 'Invalid File Format',
        description: `Only ${formatNames} formats are supported!`,
      });
      return false;
    }

    const isValidSize = file.size / 1024 / 1024 < maxSize;
    if (!isValidSize) {
      showNotification({
        type: 'error',
        message: 'File Too Large',
        description: `Image must be smaller than ${maxSize}MB!`,
      });
      return false;
    }

    // Aspect ratio ve boyut kontrolü için Promise kullanıyoruz
    return new Promise<boolean>((resolve) => {
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;

        // Maksimum boyut kontrolü
        if (maxWidth && width > maxWidth) {
          showNotification({
            type: 'error',
            message: 'Image Too Wide',
            description: `Image width must not exceed ${maxWidth}px! Current: ${width}px`,
          });
          resolve(false);
          return;
        }

        if (maxHeight && height > maxHeight) {
          showNotification({
            type: 'error',
            message: 'Image Too Tall',
            description: `Image height must not exceed ${maxHeight}px! Current: ${height}px`,
          });
          resolve(false);
          return;
        }

        // Aspect ratio kontrolü
        if (aspectRatio) {
          const imageRatio = width / height;
          const tolerance = 0.1; // %10 tolerans

          if (Math.abs(imageRatio - aspectRatio) > tolerance) {
            const expectedRatio = aspectRatio === 1 ? '1:1' : `${aspectRatio}:1`;
            showNotification({
              type: 'error',
              message: 'Invalid Aspect Ratio',
              description: `Image aspect ratio must be ${expectedRatio}! Current ratio: ${imageRatio.toFixed(2)}:1`,
            });
            resolve(false);
            return;
          }
        }

        // Tüm kontroller geçtiyse görüntüyü ayarla
        const reader = new FileReader();
        reader.onload = () => {
          if (currentLanguage) {
            setImagePreview({
              // @ts-ignore
              ...(imagePreview || ({} as MultilanguageState<string>)),
              [currentLanguage]: reader.result as string,
            });
          } else {
            setImagePreview(reader.result as string);
          }
        };
        reader.readAsDataURL(file);

        if (currentLanguage) {
          const multilangValue = value as MultilanguageState<ContentImage>;
          onChange({
            ...value,
            [currentLanguage]: {
              id: multilangValue?.[currentLanguage]?.id,
              languageId: currentLanguage,
              environmentType: 'Mobile',
              document: file,
            },
          } as MultilanguageState<ContentImage>);
        } else {
          onChange({
            id: (value as ContentImage)?.id,
            languageId: currentLanguage,
            environmentType: 'Mobile',
            document: file,
          } as ContentImage);
        }
        resolve(false);
      };

      img.onerror = () => {
        showNotification({
          type: 'error',
          message: 'Image Load Failed',
          description: 'Failed to load image!',
        });
        resolve(false);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const handleRemove = () => {
    if (typeof value === 'object' && currentLanguage) {
      const multilangValue = value as MultilanguageState<ContentImage>;
      const existingId = multilangValue?.[currentLanguage]?.id;

      const newValue = {
        ...value,
        [currentLanguage]: existingId
          ? {
              id: existingId,
              languageId: currentLanguage,
              environmentType: 'Mobile',
              document: undefined,
            }
          : undefined,
      } as MultilanguageState<ContentImage>;
      onChange(newValue);
      setImagePreview({
        // @ts-ignore koşula eklemedim o yüzden
        ...imagePreview,
        [currentLanguage]: undefined,
      });
    } else {
      setImagePreview(undefined);
      onChange(undefined);
    }
  };

  const uploadProps: UploadProps = {
    beforeUpload,
    onRemove: handleRemove,
    fileList: [], // Antd'nin kendi upload listesini gizliyoruz
    maxCount: 1,
    showUploadList: false, // Upload listesini tamamen gizliyoruz
    disabled,
    accept: acceptedFormats.join(','), // Dosya seçme ekranında sadece belirtilen formatları göster
  };

  const formatList = acceptedFormats.map((format) => format.replace('image/', '').toUpperCase()).join(', ');

  const currentPreview = typeof imagePreview === 'object' && currentLanguage ? imagePreview[currentLanguage] : imagePreview;
  const hasError = typeof error === 'object' ? (currentLanguage ? !error[currentLanguage] : false) : error;
  return (
    <ConfigProvider theme={getThemeConfig()}>
      <div className={classNames(commonStyles.container, className)}>
        {label && (
          <label className={commonStyles.label}>
            {label} {required && <span style={{ color: 'var(--color-success)' }}>*</span>}
          </label>
        )}

        <div style={{ position: 'relative' }}>
          <Upload.Dragger
            {...uploadProps}
            className={classNames({
              [commonStyles.error]: hasError,
              [commonStyles.disabled]: disabled,
            })}
            style={{
              backgroundColor: disabled ? 'var(--color-backgorund-disabled)' : 'transparent',
              borderStyle: 'solid',
              borderWidth: '1px',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              borderColor: hasError ? 'var(--color-danger)' : undefined,
            }}
          >
            {currentPreview ? (
              <div style={{ display: 'inline-block' }}>
                <img
                  // @ts-ignore
                  src={currentPreview}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }}
                />
              </div>
            ) : (
              <div>
                <div
                  style={{
                    fontSize: '32px',
                    color: 'var(--color-primary)',
                    marginBottom: '12px',
                  }}
                >
                  <UploadOutlined />
                </div>
                <p
                  style={{
                    fontSize: '16px',
                    color: 'var(--color-primary)',
                    fontWeight: 500,
                    margin: '0 0 8px 0',
                  }}
                >
                  {placeholder}
                </p>
                <p
                  style={{
                    fontSize: '14px',
                    color: 'var(--color-text-secondary)',
                    margin: '0',
                  }}
                >
                  {formatList} (max. {maxSize}MB)
                  {(maxWidth || maxHeight) && <br />}
                  {maxWidth && maxHeight && (
                    <>
                      Max. {maxWidth}x{maxHeight}px
                    </>
                  )}
                  {aspectRatio && (
                    <>
                      <br />
                      Aspect ratio: {aspectRatio === 1 ? '1:1' : `${aspectRatio}:1`}
                    </>
                  )}
                </p>
              </div>
            )}
          </Upload.Dragger>

          {imagePreview && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'transparent',
                border: 'none',
                borderRadius: '4px',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--color-text-secondary)',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                zIndex: 10,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--color-danger)';
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--color-text-secondary)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <DeleteOutlined />
            </button>
          )}
        </div>

        {hasError && <span className={commonStyles.errorMessage}>{errorMessage || 'This field is mandatory.'}</span>}
      </div>
    </ConfigProvider>
  );
};

export default ImageUpload;

